import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { homePathForRole } from "../lib/homePath";
import { newUi } from "../lib/featureFlags";
import { LoadingSplash } from "./kit/LoadingSplash";
import ForcePasswordChange from "../pages/ForcePasswordChange";
import ForcePasswordChangeNew from "../pages/ForcePasswordChangeNew";

// `bounceTo` (#1166, D215): where a role the `roles` list refuses is sent
// instead of its own home. Only for a route whose answer for those roles is a
// named page: an admin's home is /dashboard, but D215 sends an admin who opens
// /development or /performance to Every player on /coach-portal.
export default function ProtectedRoute({ children, roles, bounceTo }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <LoadingSplash testId="auth-loading" />
    );
  }
  // Issue #257: the attempted location rides along to /login, so signing in
  // finishes the journey the link started instead of discarding it. Before
  // this, a shared `/coach-portal?tab=feedback` silently became "here is the
  // dashboard" — the link appeared to work and quietly didn't do what the
  // sender meant. `location` (not just `pathname`) because the query string
  // and hash are frequently the whole point of the link.
  //
  // `state` is only ever a REQUEST: `lib/redirectTarget.js` decides whether
  // it is safe to honour, because history state is attacker-writable and an
  // unvalidated one is an open-redirect primitive.
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  // Issue #295: an account whose password was minted by an admin holds a
  // credential a second person has seen, and the API confines its session to
  // the change-password call until the holder replaces it — every other
  // request 403s. So the app is rendered as the one screen that can actually
  // do something, rather than as a shell of pages that all fail.
  //
  // A GATE, not a route, and that is the security-relevant part: there is no
  // `/change-password` URL to navigate past, and every protected page —
  // including ones added later — is behind this single check rather than
  // each remembering to make it. It sits BEFORE the `roles` branch below so
  // a flagged coach or admin is confined exactly like a flagged player,
  // instead of being redirected somewhere that would then 403.
  //
  // Nothing here handles the flag being set MID-session (an admin resetting
  // someone who is signed in): that reset revokes their sessions too (#226),
  // so the next request is a 401 and the app bounces to /login — where the
  // fresh sign-in lands them back here with the flag set.
  //
  // Being a gate is also what makes it transparent to #257's deep links: the
  // URL is still the one the link named, so replacing the password lifts the
  // gate and the page underneath renders — the destination survives THROUGH
  // the interstitial rather than being bypassed by it.
  // Issue #874 slice S3: redrawn behind newUi("signin") (S1/S2's own flag
  // — this slice adds no new one). This IS a same-line ternary, App.js's
  // own established shape — and it does NOT also gate every OTHER route
  // `<ProtectedRoute>` wraps (`/dashboard`, `/performance`, …), because
  // `flagSafeLinks.test.js` lists this file, BY NAME, on its explicit
  // `WRAPPER_TERNARY_FILES` allowlist: this `if` is one conditional early
  // return among several, in a function that returns bare `children`
  // (below) for every OTHER caller, so it never decides a route the way
  // App.js's own ternaries do. That list's own comment, and the guard
  // test next to it, pin THIS shape — one line, `if (...) return newUi(...)
  // ? …`, plus a `return children;` somewhere in this file — so rewriting
  // either one fails there, not silently. (Two earlier review rounds tried
  // to INFER this shape from code text instead of listing it explicitly;
  // both were found to silently under-gate a real gate shaped differently
  // — see that file's own header, fix round 6/7, for why an explicit list
  // replaced the inference entirely. The very first draft of this line
  // dodged an earlier version of the false positive by splitting the
  // ternary across lines — refused on review as detector evasion.)
  if (user.must_change_password) return newUi("signin") ? <ForcePasswordChangeNew /> : <ForcePasswordChange />;
  // #257: this is also where a deep link whose destination the signed-in role
  // cannot see ends up, quietly and once — the role bounce wins, and there is
  // no loop because it fires only for a user who is already authenticated.
  //
  // #384 item 3: the destination is the caller's OWN home, not a hardcoded
  // /dashboard. Two reasons, and the second is why it had to change. A coach
  // bounced off /statistics now lands on the page they actually work from
  // rather than one they cannot see; and /dashboard is itself role-guarded
  // since #384, so a hardcoded target here would bounce a coach into a route
  // that bounces them straight back — a loop, in the one branch of this file
  // whose whole comment says there isn't one.
  if (roles && !roles.includes(user.role)) return <Navigate to={bounceTo || homePathForRole(user.role)} replace />;
  return children;
}
